/* eslint-disable react/no-direct-mutation-state*/
import React, { Component } from 'react';
import { bool, string } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { isUploadImageOverLimitError } from '../../util/errors';
import {
  Form,
  Avatar,
  ImageFromFile,
  IconSpinner,
  FieldTextInput,
} from '../../components';

import css from './ProfileSettingsForm.css';
import { faLock, faMobile, faPaperclip, faUser } from '@fortawesome/free-solid-svg-icons';
import Button from '../../components/Button/Button';
import Geocoder from './KeyboardGeocoder';
import ReactMapGL from 'react-map-gl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FieldTip from '../../custom-components/elements/field-tip/FieldTip';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTwitter,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import { faVideo, faCompass, faClock } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import { decode, encode } from 'html-entities';
import ImageCropModal from './ImageCropModal';
import axios from 'axios';
import { getYoutubeThumbnail } from '../../util/gallery';

const ACCEPT_IMAGES = 'image/*';
const UPLOAD_CHANGE_DELAY = 2000;

const mapAccess = {
  mapboxApiAccessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
};

const mapStyle = {
  width: '100%',
  height: 350,
};

const queryParams = {};

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);
    this.uploadDelayTimeoutId = null;
    this.state = {
      uploadDelay: false,
      viewport: {},
      selectedLocation: '',
      bioHtml: '',
      cropModalOpen: false,
      cropImageSrc: null,
      cropFileName: null,
      pendingFormChange: null,
      submitAttempted: false,
      aiNotes: '',
      aiBusy: false,
      aiError: null,
      aiAvailable: true,
      showBioStructureHelp: false,
      profileReview: null,
      profileReviewBusy: false,
      profileReviewError: null,
    };
    this.submittedValues = {};
    this.onBioChange = this.onBioChange.bind(this);
    this.handleCropCancel = this.handleCropCancel.bind(this);
    this.handleCropConfirm = this.handleCropConfirm.bind(this);
    this.callBioAssistant = this.callBioAssistant.bind(this);
    this.callProfileReview = this.callProfileReview.bind(this);
    this.applyProfileSuggestion = this.applyProfileSuggestion.bind(this);
  }

  handleCropCancel() {
    if (this.state.cropImageSrc) {
      window.URL.revokeObjectURL(this.state.cropImageSrc);
    }
    this.setState({
      cropModalOpen: false,
      cropImageSrc: null,
      cropFileName: null,
      pendingFormChange: null,
    });
  }

  handleCropConfirm(croppedFile) {
    const { pendingFormChange } = this.state;
    if (pendingFormChange && croppedFile) {
      pendingFormChange(croppedFile);
    }
    if (this.state.cropImageSrc) {
      window.URL.revokeObjectURL(this.state.cropImageSrc);
    }
    this.setState({
      cropModalOpen: false,
      cropImageSrc: null,
      cropFileName: null,
      pendingFormChange: null,
    });
  }

  componentDidMount() {
    const { currentUser } = this.props;
    this.setState({ bioHtml: currentUser.attributes.profile.bio });

    // Cheap, unauthenticated probe — hides the AI bio assistant entirely
    // when GEMINI_API_KEY isn't configured, instead of showing buttons
    // that are guaranteed to fail. Fails closed: if the probe itself
    // can't be reached, treat the assistant as unavailable too.
    axios
      .get('/api/ai/optimize-bio/status')
      .then(res => {
        if (!res.data || res.data.available !== true) {
          this.setState({ aiAvailable: false });
        }
      })
      .catch(() => this.setState({ aiAvailable: false }));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.uploadInProgress && !this.props.uploadInProgress) {
      this.setState({ uploadDelay: true });
      this.uploadDelayTimeoutId = window.setTimeout(() => {
        this.setState({ uploadDelay: false });
      }, UPLOAD_CHANGE_DELAY);
    }
    var locationField = document.querySelector('#location_search input');
    locationField.setAttribute('placeholder', 'Location');
  }

  componentWillUnmount() {
    window.clearTimeout(this.uploadDelayTimeoutId);
    if (this.state.cropImageSrc) {
      window.URL.revokeObjectURL(this.state.cropImageSrc);
    }
  }

  onSelected = (viewport, item) => {
    this.setState({ viewport: viewport });
    this.state.selectedLocation = item.place_name;
  };

  onBioChange(html) {
    const htmlText = html.replace(/<[^>]+>/g, '');
    this.setState({ bioHtml: encode(html) });
  }

  // Calls the Gemini-backed bio assistant (server/api/ai/optimizeBio.js).
  // mode 'draft' generates a first pass from displayName/location/aiNotes;
  // mode 'optimize' polishes whatever is already in the editor. The key
  // itself never reaches the browser — this just posts to our own server.
  async callBioAssistant(mode, meta) {
    if (this.state.aiBusy) return;
    this.setState({ aiBusy: true, aiError: null });
    try {
      const plainBio = decode(this.state.bioHtml || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const res = await axios.post('/api/ai/optimize-bio', {
        mode,
        text: plainBio,
        context: this.state.aiNotes,
        displayName: meta.displayName,
        location: meta.location,
      });
      const generated = res.data && res.data.text;
      if (generated) {
        // Server already returns formatted HTML (intro + bullets, see
        // geminiClient.js's formatAiText) — hand it to the editor as-is.
        this.onBioChange(generated);
      } else {
        this.setState({ aiError: 'Could not generate text right now — please try again.' });
      }
    } catch (err) {
      const status = err.response && err.response.status;
      // 429 now means one of two things: our own per-user rate limit (body
      // { error: 'rate_limited', retryAfterSeconds }) — a normal, expected
      // event, so just tell the coach to wait — or, in principle, upstream
      // Gemini quota exhaustion. 503 = key missing/model overloaded, no
      // response at all = endpoint unreachable — those mean the assistant
      // isn't usable right now, so hide it rather than leaving a button up
      // that will just keep failing. A plain 400/other error (e.g. "text
      // required") is a one-off — keep the UI, show the message.
      const isRateLimited = status === 429 && err.response.data && err.response.data.error === 'rate_limited';
      if (isRateLimited) {
        const retryAfter = err.response.data.retryAfterSeconds;
        this.setState({
          aiError: retryAfter
            ? `You've hit the AI assist limit — try again in ${retryAfter}s.`
            : "You've hit the AI assist limit — try again shortly.",
        });
      } else if (!err.response || status === 429 || status === 503) {
        this.setState({ aiAvailable: false });
      } else {
        this.setState({ aiError: 'Could not generate text right now — please try again.' });
      }
    } finally {
      this.setState({ aiBusy: false });
    }
  }

  // Calls the Gemini-backed profile reviewer (server/api/ai/improveProfile.js)
  // — scores the bio against Viveka's profile rubric and returns strengths,
  // the top improvements, and a rewritten version, instead of just silently
  // rewriting the bio the way "Optimize" used to.
  async callProfileReview(meta) {
    if (this.state.profileReviewBusy) return;
    this.setState({ profileReviewBusy: true, profileReviewError: null, profileReview: null });
    try {
      const plainBio = decode(this.state.bioHtml || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const res = await axios.post('/api/ai/improve-bio', {
        text: plainBio,
        displayName: meta.displayName,
        location: meta.location,
      });
      if (res.data) {
        this.setState({
          profileReview: {
            score: res.data.score,
            strengths: res.data.strengths || [],
            improvements: res.data.improvements || [],
            suggestedText: res.data.suggestedText || '',
          },
        });
      } else {
        this.setState({ profileReviewError: 'Could not review your profile right now — please try again.' });
      }
    } catch (err) {
      const status = err.response && err.response.status;
      const isRateLimited = status === 429 && err.response.data && err.response.data.error === 'rate_limited';
      if (isRateLimited) {
        const retryAfter = err.response.data.retryAfterSeconds;
        this.setState({
          profileReviewError: retryAfter
            ? `You've hit the AI assist limit — try again in ${retryAfter}s.`
            : "You've hit the AI assist limit — try again shortly.",
        });
      } else if (!err.response || status === 429 || status === 503) {
        this.setState({ aiAvailable: false });
      } else {
        this.setState({ profileReviewError: 'Could not review your profile right now — please try again.' });
      }
    } finally {
      this.setState({ profileReviewBusy: false });
    }
  }

  applyProfileSuggestion() {
    const suggestion = this.state.profileReview && this.state.profileReview.suggestedText;
    if (!suggestion) return;
    // suggestedText is already raw HTML (see formatAiText in
    // geminiClient.js) — onBioChange handles encoding it for storage.
    this.onBioChange(suggestion);
    this.setState({ profileReview: null });
  }

  render() {
    const { viewport } = this.state;
    return (
      <FinalForm
        {...this.props}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            onImageUpload,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            values,
            errors,
            hasValidationErrors,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);

          const firstNamePlaceholder = intl.formatMessage({ id: 'ProfileSettingsForm.firstNamePlaceholder' });
          const firstNameRequiredMessage = intl.formatMessage({ id: 'ProfileSettingsForm.firstNameRequired' });
          const firstNameRequired = validators.required(firstNameRequiredMessage);

          const lastNamePlaceholder = intl.formatMessage({ id: 'ProfileSettingsForm.lastNamePlaceholder' });
          const lastNameRequiredMessage = intl.formatMessage({ id: 'ProfileSettingsForm.lastNameRequired' });
          const lastNameRequired = validators.required(lastNameRequiredMessage);

          const displayNamePlaceholder = intl.formatMessage({ id: 'ProfileSettingsForm.displayNamePlaceholder' });
          const displayNameRequiredMessage = intl.formatMessage({ id: 'ProfileSettingsForm.displayNameRequired' });
          const displayNameRequired = validators.required(displayNameRequiredMessage);

          const phonePlaceholder = intl.formatMessage({ id: 'ProfileSettingsForm.phonePlaceholder' });
          const phoneRequiredMessage = intl.formatMessage({ id: 'ProfileSettingsForm.phoneRequired' });
          const phoneRequired = validators.required(phoneRequiredMessage);

          const bioPlaceholder = intl.formatMessage({ id: 'ProfileSettingsForm.bioPlaceholder' });

          const uploadingOverlay =
            uploadInProgress || this.state.uploadDelay ? (
              <div className={css.uploadingImageOverlay}>
                <IconSpinner />
              </div>
            ) : null;

          const hasUploadError = !!uploadImageError && !uploadInProgress;
          const errorClasses = classNames({ [css.avatarUploadError]: hasUploadError });
          const transientUserProfileImage = profileImage.uploadedImage || user.profileImage;
          const transientUser = { ...user, profileImage: transientUserProfileImage };

          const fileExists = !!profileImage.file;
          const fileUploadInProgress = uploadInProgress && fileExists;
          const delayAfterUpload = profileImage.imageId && this.state.uploadDelay;
          const imageFromFile =
            fileExists && (fileUploadInProgress || delayAfterUpload) ? (
              <ImageFromFile
                id={profileImage.id}
                className={errorClasses}
                rootClassName={css.uploadingImage}
                aspectRatioClassName={css.squareAspectRatio}
                file={profileImage.file}
              >
                {uploadingOverlay}
              </ImageFromFile>
            ) : null;

          const avatarClasses = classNames(errorClasses, css.avatar, {
            [css.avatarInvisible]: this.state.uploadDelay,
          });

          const avatarComponent =
            !fileUploadInProgress && profileImage.imageId ? (
              <div className={css.avatarBox}>
                <Avatar
                  className={avatarClasses}
                  renderSizes="(max-width: 767px) 96px, 240px"
                  user={transientUser}
                  disableProfileLink
                />
              </div>
            ) : null;

          const onImageClick = () => {
            var imageField = document.querySelector('#profileImage');
            imageField.click();
          };

          const chooseAvatarLabel =
            profileImage.imageId || fileUploadInProgress ? (
              <div className={css.avatarContainer}>
                {imageFromFile}
                {avatarComponent}
                <div className={css.changeAvatarContainer}>
                  <div className={css.changeAvatar} onClick={onImageClick}>
                    <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
                  </div>
                </div>
              </div>
            ) : (
              <div onClick={onImageClick} className={css.avatarPlaceholder}>
                <div className={css.avatarPlaceholderText}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
                </div>
                <div className={css.avatarPlaceholderTextMobile}>
                  <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
                </div>
              </div>
            );

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const fieldLabels = {
            firstName: 'First Name',
            lastName: 'Last Name',
            displayName: 'Display Name',
            phone: 'Phone Number',
          };
          const missingFieldLabels = Object.keys(errors || {})
            .filter(key => fieldLabels[key])
            .map(key => fieldLabels[key]);
          const validationSummary =
            this.state.submitAttempted && invalid && missingFieldLabels.length > 0 ? (
              <div className={css.error}>
                Please fill in the required field
                {missingFieldLabels.length === 1 ? '' : 's'}:{' '}
                <strong>{missingFieldLabels.join(', ')}</strong>.
              </div>
            ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;

          const quillModules = {
            toolbar: [
              [{ header: [1, 2, false] }],
              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
              [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
              ['link'],
              ['clean'],
            ],
          };

          const quillFormats = [
            'header',
            'bold',
            'italic',
            'underline',
            'strike',
            'blockquote',
            'list',
            'bullet',
            'indent',
            'link',
          ];

          return (
            <React.Fragment>
              <ImageCropModal
                isOpen={this.state.cropModalOpen}
                imageSrc={this.state.cropImageSrc}
                fileName={this.state.cropFileName}
                onCancel={this.handleCropCancel}
                onConfirm={this.handleCropConfirm}
              />
              <Form
                className={classes}
                onSubmit={e => {
                  this.setState({ submitAttempted: true });
                  if (hasValidationErrors) {
                    if (e && typeof e.preventDefault === 'function') {
                      e.preventDefault();
                    }
                    Object.keys(errors || {}).forEach(fieldName => {
                      try {
                        form.blur(fieldName);
                        form.focus(fieldName);
                      } catch (_err) {
                        // ignore
                      }
                    });
                    const firstErrorField = Object.keys(errors || {})[0];
                    if (firstErrorField) {
                      const el = document.getElementById(firstErrorField);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        if (typeof el.focus === 'function') {
                          el.focus({ preventScroll: true });
                        }
                      }
                    }
                    return;
                  }
                  values.location = this.state.selectedLocation;
                  values.bio = this.state.bioHtml;
                  this.submittedValues = values;
                  handleSubmit(e);
                }}
              >
                <div className={css.outerBase}>

                  {/* Card 1: Basic Information */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </span>
                        Basic Information
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <div className={css.requiredNote}>* Required fields</div>
                      <div className={css.twoCol}>
                        <div>
                          <div className={css.requiredFieldLabel}>
                            First Name <span className={css.requiredLabel}>*</span>
                          </div>
                          <div className={css.form_field}>
                            <FieldTextInput
                              className={css.firstName}
                              type="text"
                              id="firstName"
                              icon={faUser}
                              name="firstName"
                              placeholder={firstNamePlaceholder}
                              validate={firstNameRequired}
                            />
                          </div>
                        </div>
                        <div>
                          <div className={css.requiredFieldLabel}>
                            Last Name <span className={css.requiredLabel}>*</span>
                          </div>
                          <div className={css.form_field}>
                            <FieldTextInput
                              type="text"
                              id="lastName"
                              icon={faUser}
                              name="lastName"
                              placeholder={lastNamePlaceholder}
                              validate={lastNameRequired}
                            />
                          </div>
                        </div>
                      </div>
                      <div className={css.requiredFieldLabel}>
                        Display Name <span className={css.requiredLabel}>*</span>
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="text"
                          id="displayName"
                          icon={faUser}
                          name="displayName"
                          placeholder={displayNamePlaceholder}
                          validate={displayNameRequired}
                        />
                        <FieldTip
                          className={css.fieldTip}
                          message={<FormattedMessage id="ProfileSettingsForm.tip_displayname" />}
                        />
                      </div>
                      <div className={css.requiredFieldLabel}>
                        Phone Number <span className={css.requiredLabel}>*</span>
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="text"
                          id="phone"
                          icon={faMobile}
                          name="phone"
                          placeholder={phonePlaceholder}
                          validate={phoneRequired}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Profile Photo */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                            <circle cx="12" cy="13" r="4" />
                          </svg>
                        </span>
                        Profile Photo
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <Field
                        accept={ACCEPT_IMAGES}
                        id="profileImage"
                        name="profileImage"
                        label={chooseAvatarLabel}
                        type="file"
                        form={null}
                        uploadImageError={uploadImageError}
                        disabled={uploadInProgress}
                      >
                        {fieldProps => {
                          const { accept, id, input, label, disabled, uploadImageError } = fieldProps;
                          const { name, type } = input;
                          const onChange = e => {
                            const file = e.target.files[0];
                            e.target.value = '';
                            if (file == null) return;
                            const objectUrl = window.URL.createObjectURL(file);
                            this.setState({
                              cropModalOpen: true,
                              cropImageSrc: objectUrl,
                              cropFileName: file.name,
                              pendingFormChange: croppedFile => {
                                form.change(`profileImage`, croppedFile);
                                form.blur(`profileImage`);
                                const tempId = `${croppedFile.name}_${Date.now()}`;
                                onImageUpload({ id: tempId, file: croppedFile });
                              },
                            });
                          };
                          let error = null;
                          if (isUploadImageOverLimitError(uploadImageError)) {
                            error = (
                              <div className={css.error}>
                                <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
                              </div>
                            );
                          } else if (uploadImageError) {
                            error = (
                              <div className={css.error}>
                                <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
                              </div>
                            );
                          }
                          return (
                            <div className={css.uploadAvatarWrapper}>
                              {label}
                              <input
                                accept={accept}
                                id={id}
                                name={name}
                                className={css.uploadAvatarInput}
                                disabled={disabled}
                                onChange={onChange}
                                type={type}
                              />
                              {error}
                            </div>
                          );
                        }}
                      </Field>
                    </div>
                  </div>

                  {/* Card 3: Location */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                          </svg>
                        </span>
                        Location
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <div id="location_search" className={css.locationSearchBox}>
                        <FontAwesomeIcon className={css.locationIcon} icon={faLock} />
                        <Geocoder
                          className={css.locationSearch}
                          name="location"
                          placeholder={'Search'}
                          initialInputValue={values.location}
                          updateInputOnSelect={true}
                          {...mapAccess}
                          onSelected={this.onSelected}
                          viewport={viewport}
                          hideOnSelect={true}
                          queryParams={queryParams}
                        />
                      </div>
                      <div className={css.locationMap}>
                        <ReactMapGL
                          {...mapAccess}
                          {...viewport}
                          {...mapStyle}
                          mapStyle="mapbox://styles/mapbox/streets-v8"
                          onViewportChange={newViewport => this.setState({ viewport: newViewport })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 4: About You */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </span>
                        About You
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <div className={css.top_visibility_message}>
                        <FormattedMessage id="ProfileSettingsForm.visibility_message" />
                      </div>

                      <div className={css.bioGuidance}>
                        <p className={css.bioGuidanceTitle}>Write your professional coach overview</p>
                        <p className={css.bioGuidanceSubtitle}>
                          Help prospective clients and organizations quickly understand who you help,
                          what you help them achieve, and why you're the right coach for them.
                        </p>
                        <p className={css.bioGuidanceLead}>In 150–250 words, include:</p>
                        <ul className={css.bioGuidanceList}>
                          <li><strong>Who you coach</strong> — the leaders, professionals, teams, or organizations you work with</li>
                          <li><strong>What brings them to you</strong> — the challenges, transitions, or opportunities you specialize in</li>
                          <li><strong>What you help create</strong> — the outcomes or transformation clients work toward</li>
                          <li><strong>How you coach</strong> — your approach, philosophy, or methodology and what makes it distinctive</li>
                          <li><strong>Why clients can trust you</strong> — relevant leadership experience, industry expertise, credentials, or lived experience</li>
                          <li><strong>What it's like to work with you</strong> — one sentence that gives clients a sense of your style</li>
                        </ul>
                        <p className={css.bioGuidanceNote}>
                          Write in the first person, be specific, and sound like yourself. Avoid
                          generic claims, jargon, long credential lists, and promises of guaranteed
                          results.
                        </p>
                        <button
                          type="button"
                          className={css.bioStructureToggle}
                          onClick={() =>
                            this.setState({ showBioStructureHelp: !this.state.showBioStructureHelp })
                          }
                        >
                          {this.state.showBioStructureHelp
                            ? 'Hide example structure'
                            : 'Need help? Use this structure'}
                        </button>
                        {this.state.showBioStructureHelp && (
                          <div className={css.bioStructureTemplate}>
                            <p>
                              I work with <em>[specific type of client]</em> who are navigating{' '}
                              <em>[specific challenges/context]</em>.
                            </p>
                            <p>
                              My coaching helps them <em>[2–3 meaningful outcomes]</em>.
                            </p>
                            <p>
                              My approach combines <em>[method/experience/philosophy]</em>, with a
                              particular focus on <em>[your distinctive area of expertise]</em>.
                            </p>
                            <p>
                              I bring <em>[relevant credibility/experience/credentials]</em> to my
                              work, giving me particular insight into{' '}
                              <em>[relevant client environment]</em>.
                            </p>
                            <p>
                              Clients can expect me to be{' '}
                              <em>[2–3 qualities describing your coaching style]</em>.
                            </p>
                          </div>
                        )}
                      </div>

                      {this.state.aiAvailable && (
                      <div className={css.aiAssistant}>
                        <p className={css.aiHint}>
                          Need help getting started? Add a few notes (optional), then have AI draft
                          it for you — or get a rubric-based review of what you've already written.
                        </p>
                        <input
                          type="text"
                          className={css.aiNotesInput}
                          placeholder="Optional: your specialty, experience, coaching style…"
                          value={this.state.aiNotes}
                          onChange={e => this.setState({ aiNotes: e.target.value })}
                          disabled={this.state.aiBusy}
                        />
                        <div className={css.aiButtons}>
                          <button
                            type="button"
                            className={css.aiButton}
                            disabled={this.state.aiBusy || this.state.profileReviewBusy}
                            onClick={() =>
                              this.callBioAssistant('draft', {
                                displayName: values.displayName,
                                location: this.state.selectedLocation || values.location,
                              })
                            }
                          >
                            {this.state.aiBusy ? 'Working…' : '✨ Help me write'}
                          </button>
                          <button
                            type="button"
                            className={css.aiButton}
                            disabled={
                              this.state.profileReviewBusy ||
                              this.state.aiBusy ||
                              !decode(this.state.bioHtml || '')
                                .replace(/<[^>]+>/g, '')
                                .trim()
                            }
                            onClick={() =>
                              this.callProfileReview({
                                displayName: values.displayName,
                                location: this.state.selectedLocation || values.location,
                              })
                            }
                          >
                            {this.state.profileReviewBusy ? 'Reviewing…' : '✨ Improve with AI'}
                          </button>
                        </div>
                        {this.state.aiError && (
                          <div className={css.aiError}>{this.state.aiError}</div>
                        )}
                        {this.state.profileReviewError && (
                          <div className={css.aiError}>{this.state.profileReviewError}</div>
                        )}
                      </div>
                      )}
                      <div className={css.about}>
                        <ReactQuill
                          placeholder={bioPlaceholder}
                          value={decode(this.state.bioHtml)}
                          onChange={e => this.onBioChange(e)}
                          modules={quillModules}
                          formats={quillFormats}
                          style={{ height: 400, paddingBottom: 40 }}
                        />
                      </div>
                      <p className={css.bioWordCountHint}>
                        Recommended: 150–250 words · Write in first person · Be specific and
                        outcome-focused
                        {(() => {
                          const wordCount = decode(this.state.bioHtml || '')
                            .replace(/<[^>]+>/g, ' ')
                            .trim()
                            .split(/\s+/)
                            .filter(Boolean).length;
                          return wordCount > 0 ? ` · ${wordCount} words so far` : '';
                        })()}
                      </p>

                      {this.state.profileReview && (
                        <div className={css.profileReviewCard}>
                          <div className={css.profileReviewHeader}>
                            <span className={css.profileReviewScore}>
                              Profile strength: {this.state.profileReview.score}/100
                            </span>
                            <button
                              type="button"
                              className={css.profileReviewDismiss}
                              onClick={() => this.setState({ profileReview: null })}
                            >
                              Dismiss
                            </button>
                          </div>
                          {this.state.profileReview.strengths.length > 0 && (
                            <div className={css.profileReviewSection}>
                              <p className={css.profileReviewSectionTitle}>What's strong</p>
                              <ul>
                                {this.state.profileReview.strengths.map((s, i) => (
                                  <li key={i}>{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {this.state.profileReview.improvements.length > 0 && (
                            <div className={css.profileReviewSection}>
                              <p className={css.profileReviewSectionTitle}>
                                {this.state.profileReview.improvements.length} improvement
                                {this.state.profileReview.improvements.length === 1 ? '' : 's'}
                              </p>
                              <ul>
                                {this.state.profileReview.improvements.map((s, i) => (
                                  <li key={i}>{s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {this.state.profileReview.suggestedText && (
                            <div className={css.profileReviewSection}>
                              <p className={css.profileReviewSectionTitle}>Suggested optimized version</p>
                              <div
                                className={css.profileReviewSuggestion}
                                // Safe: produced by formatAiText() server-side from an escaped
                                // source string, so only our own <p>/<ul>/<li>/<strong> tags exist.
                                dangerouslySetInnerHTML={{
                                  __html: this.state.profileReview.suggestedText,
                                }}
                              />
                              <button
                                type="button"
                                className={css.aiButton}
                                onClick={this.applyProfileSuggestion}
                              >
                                Use this version
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card 5: Video Introduction */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <FontAwesomeIcon icon={faVideo} style={{ fontSize: 15 }} />
                        </span>
                        Video Introduction
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <p className={css.bioGuidanceSubtitle}>
                        Add a short YouTube video introducing yourself. A face-to-camera intro
                        helps prospective clients get a feel for your style before they book —
                        it will appear on your public profile.
                      </p>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="text"
                          id="videoIntroUrl"
                          icon={faVideo}
                          name="videoIntroUrl"
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                      {values.videoIntroUrl && getYoutubeThumbnail(values.videoIntroUrl) && (
                        <div className={css.videoPreviewWrap}>
                          <img
                            className={css.videoPreviewThumb}
                            src={getYoutubeThumbnail(values.videoIntroUrl)}
                            alt="Video introduction preview"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card 5b: Your Approach — a coach's own named methodology
                      is a trust signal worth its own field rather than being
                      buried inside the free-text bio. All optional. */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <FontAwesomeIcon icon={faCompass} style={{ fontSize: 15 }} />
                        </span>
                        Your Approach
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <p className={css.bioGuidanceSubtitle}>
                        If you coach with a named framework or method, add it here so it
                        stands out on your profile instead of getting lost in your bio. Optional.
                      </p>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="text"
                          id="methodologyName"
                          name="methodologyName"
                          label="Method name"
                          placeholder="e.g. The Integration Breakthrough Method™"
                        />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="text"
                          id="methodologyTagline"
                          name="methodologyTagline"
                          label="Short tagline (optional)"
                          placeholder="e.g. SEE. BREATHE. STAY."
                        />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="textarea"
                          id="methodologyDescription"
                          name="methodologyDescription"
                          label="Describe it in your own words (optional)"
                          placeholder="A sentence or two on how your method works."
                        />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="number"
                          id="yearsExperience"
                          name="yearsExperience"
                          label="Years of experience (optional)"
                          placeholder="e.g. 20"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 5c: Response time — sets client expectations before
                      they book. Optional; the profile hides this line when
                      it isn't set rather than guessing. */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <FontAwesomeIcon icon={faClock} style={{ fontSize: 15 }} />
                        </span>
                        Response Time
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <p className={css.bioGuidanceSubtitle}>
                        Roughly how many hours does it usually take you to reply to a new
                        client enquiry? Shown on your profile to set expectations. Optional.
                      </p>
                      <div className={css.form_field}>
                        <FieldTextInput
                          type="number"
                          id="replyTimeHours"
                          name="replyTimeHours"
                          label="Usually replies within (hours)"
                          placeholder="e.g. 24"
                          min="1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card 6: Social Links */}
                  <div className={css.settingsBlock}>
                    <div className={css.blockHeader}>
                      <p className={css.blockTitle}>
                        <span className={css.blockIcon}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                          </svg>
                        </span>
                        Social Links
                      </p>
                    </div>
                    <div className={css.blockBody}>
                      <div className={css.form_field}>
                        <FieldTextInput type="text" id="linedin" icon={faLinkedin} name="linkedin" placeholder="Your LinkedIn profile URL" />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput type="text" id="facebook" icon={faFacebook} name="facebook" placeholder="Facebook URL" />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput type="text" id="twitter" icon={faTwitter} name="twitter" placeholder="Twitter URL" />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput type="text" id="instagram" icon={faInstagram} name="instagram" placeholder="Instagram URL" />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput type="text" id="youtube" icon={faYoutube} name="youtube" placeholder="YouTube URL" />
                      </div>
                      <div className={css.form_field}>
                        <FieldTextInput type="text" id="other" icon={faPaperclip} name="other" placeholder="Other URL" />
                      </div>
                    </div>
                  </div>

                  {/* Validation + save */}
                  {validationSummary}
                  {submitError}
                  <div className={css.actionBar}>
                    <Button
                      className={css.submitButton}
                      type="submit"
                      inProgress={submitInProgress}
                    >
                      <FormattedMessage id="ProfileSettingsForm.saveChanges" />
                    </Button>
                  </div>

                </div>
              </Form>
            </React.Fragment>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

ProfileSettingsFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  updateProfileReady: bool,
  intl: intlShape.isRequired,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
